import { useState } from 'react';
import {
  Shield,
  Lock,
  Key,
  Clock,
  Users,
  Loader2,
  Info,
  Fingerprint,
  DollarSign,
  UserCog,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useSecuritySettings, type SecuritySettings } from '@/hooks/useSecuritySettings';
import { usePermissions } from '@/hooks/usePermissions';
import { useOwner2FAApproval } from '@/hooks/useOwner2FAApproval';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';

const USER_SWITCHING_OPTIONS = [
  { value: 'never', label: 'Never require password', description: 'Users can switch freely without authentication' },
  { value: 'barcode_exempt', label: "Don't require password with barcode", description: 'Password required except when switching via barcode scan' },
  { value: 'require_password_higher_privilege', label: 'Require password for higher privilege', description: 'Password only required when switching to a more privileged role' },
  { value: 'always', label: 'Always require password', description: 'Password required for every user switch' },
];

const AUTH_METHOD_OPTIONS = [
  { value: 'password', label: 'Username and password' },
  { value: 'mfa', label: 'Username and password with MFA' },
];

const INACTIVITY_TIMEOUT_OPTIONS = [
  { value: 'null', label: 'Disabled' },
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
];

const SESSION_TIMEOUT_OPTIONS = [
  { value: '12', label: '12 hours' },
  { value: '24', label: '24 hours (recommended)' },
  { value: '48', label: '48 hours' },
  { value: '72', label: '3 days' },
  { value: '168', label: '7 days' },
];

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  superadmin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  inventory_manager: 'Inventory Manager',
  inventory: 'Inventory',
  cashier: 'Cashier',
  employee: 'Employee',
};

const LOGIN_ACCESS_OPTIONS = [
  { value: 'full', label: 'Has access to log in' },
  { value: 'switch_only', label: 'Only via user switching' },
];

export default function Security() {
  const {
    securitySettings,
    roleAuthSettings,
    isLoading,
    updateSecuritySettings,
    updateRoleAuthSetting,
    standardRoles,
    getEffectiveAuthMethod,
  } = useSecuritySettings();

  const { profile } = useAuth();
  const { hasPermission } = usePermissions();
  const { owner2FAEnabled } = useOwner2FAApproval();
  const { formatCurrency } = useCurrency();
  const canEdit = hasPermission('security.edit');
  const canEditSessionTimeout = canEdit && ['superadmin', 'primary_admin', 'owner'].includes(profile?.role || '');

  const [saving, setSaving] = useState<string | null>(null);
  const [owner2FASettings, setOwner2FASettings] = useState({
    enabled: owner2FAEnabled,
    voidThreshold: 100,
    refundThreshold: 50,
    requireForRoleChanges: true,
    requireFor2FADisable: true,
  });

  const handleUpdateSettings = async (key: keyof SecuritySettings, value: any) => {
    setSaving(key);
    try {
      await updateSecuritySettings.mutateAsync({ [key]: value });
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateRoleSetting = async (role: string, field: string, value: any) => {
    setSaving(`${role}-${field}`);
    try {
      await updateRoleAuthSetting.mutateAsync({
        role,
        updates: { [field]: value },
      });
    } finally {
      setSaving(null);
    }
  };

  // Filter out superadmin from display for non-superadmins
  const displayRoles = standardRoles.filter(r => {
    if (r === 'superadmin' && !hasPermission('audit.view_superadmin')) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Security Settings</h2>
          <p className="text-sm text-muted-foreground">
            Manage access and authentication settings for your store
          </p>
        </div>
        {!canEdit && (
          <Badge variant="secondary" className="gap-1">
            <Lock className="h-3 w-3" />
            View Only
          </Badge>
        )}
      </div>

      {/* User Switching Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Switching User Accounts
          </CardTitle>
          <CardDescription>
            Select a requirement for switching user accounts once someone has logged in
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {USER_SWITCHING_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                securitySettings.user_switching === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50',
                !canEdit && 'cursor-not-allowed opacity-60'
              )}
              onClick={() => canEdit && handleUpdateSettings('user_switching', option.value)}
            >
              <div className={cn(
                'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center',
                securitySettings.user_switching === option.value
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground/50'
              )}>
                {securitySettings.user_switching === option.value && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{option.label}</p>
                <p className="text-xs text-muted-foreground">{option.description}</p>
              </div>
              {saving === 'user_switching' && securitySettings.user_switching === option.value && (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Authentication Method */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="h-4 w-4" />
            Authentication Method
          </CardTitle>
          <CardDescription>
            Manage your default authentication method when users enter their password to access the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {AUTH_METHOD_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                securitySettings.default_auth_method === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50',
                !canEdit && 'cursor-not-allowed opacity-60'
              )}
              onClick={() => canEdit && handleUpdateSettings('default_auth_method', option.value)}
            >
              <div className={cn(
                'h-4 w-4 rounded-full border-2 flex items-center justify-center',
                securitySettings.default_auth_method === option.value
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground/50'
              )}>
                {securitySettings.default_auth_method === option.value && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{option.label}</span>
              {option.value === 'mfa' && (
                <Badge variant="outline" className="ml-auto text-xs">
                  More Secure
                </Badge>
              )}
              {saving === 'default_auth_method' && securitySettings.default_auth_method === option.value && (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Inactivity Re-authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Inactivity Re-Authentication
          </CardTitle>
          <CardDescription>
            After a period of inactivity the session will be paused. You can require users to re-authenticate to resume working.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div>
              <Label className="font-medium">Require logging in after inactivity</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Lock the session and require password to continue
              </p>
            </div>
            <div className="flex items-center gap-2">
              {saving === 'require_reauth_on_inactivity' && (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              )}
              <Switch
                checked={securitySettings.require_reauth_on_inactivity}
                onCheckedChange={(checked) => handleUpdateSettings('require_reauth_on_inactivity', checked)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {securitySettings.require_reauth_on_inactivity && (
            <div className="space-y-2">
              <Label>Inactivity timeout</Label>
              <Select
                value={securitySettings.inactivity_timeout_minutes?.toString() || 'null'}
                onValueChange={(value) => 
                  handleUpdateSettings('inactivity_timeout_minutes', value === 'null' ? null : parseInt(value))
                }
                disabled={!canEdit}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INACTIVITY_TIMEOUT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Timeout */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Auto-Logout After Inactivity
          </CardTitle>
          <CardDescription>
            Automatically log users out after prolonged inactivity to prevent long-lived sessions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label>Inactivity logout window</Label>
            <Select
              value={securitySettings.session_timeout_hours?.toString() || '24'}
              onValueChange={(value) => handleUpdateSettings('session_timeout_hours', parseInt(value))}
              disabled={!canEditSessionTimeout}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SESSION_TIMEOUT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canEditSessionTimeout && (
              <p className="text-xs text-muted-foreground">
                Only business owners or superadmins can change this setting.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role-Specific Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Role-Specific Settings
          </CardTitle>
          <CardDescription>
            Ensure your account is secure by restricting access to specific roles
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[200px]">Role</TableHead>
                  <TableHead>Log in access</TableHead>
                  <TableHead>Authentication method</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRoles.map((role) => {
                  const roleSetting = roleAuthSettings.find(r => r.role === role);
                  const effectiveAuthMethod = getEffectiveAuthMethod(role);
                  const isHighPrivilege = ['superadmin', 'owner', 'admin'].includes(role);
                  
                  return (
                    <TableRow key={role}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {ROLE_DISPLAY_NAMES[role] || role}
                          {isHighPrivilege && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Shield className="h-3.5 w-3.5 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>High privilege role</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isHighPrivilege ? (
                          <span className="text-sm text-muted-foreground">Has access to log in</span>
                        ) : (
                          <Select
                            value={roleSetting?.login_access || 'full'}
                            onValueChange={(value) => handleUpdateRoleSetting(role, 'login_access', value)}
                            disabled={!canEdit}
                          >
                            <SelectTrigger className="w-[200px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {LOGIN_ACCESS_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={roleSetting?.auth_method || 'default'}
                          onValueChange={(value) => handleUpdateRoleSetting(role, 'auth_method', value)}
                          disabled={!canEdit}
                        >
                          <SelectTrigger className="w-[280px] h-8">
                            <SelectValue>
                              {roleSetting?.auth_method === 'default' || !roleSetting?.auth_method
                                ? `Default setting (${effectiveAuthMethod === 'mfa' ? 'MFA' : 'Password'})`
                                : roleSetting.auth_method === 'mfa'
                                  ? 'Username and password with MFA'
                                  : 'Username and password'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">
                              Default setting ({securitySettings.default_auth_method === 'mfa' ? 'MFA' : 'Password'})
                            </SelectItem>
                            <SelectItem value="password">Username and password</SelectItem>
                            <SelectItem value="mfa">Username and password with MFA</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Owner 2FA Approval */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Fingerprint className="h-4 w-4" />
            Owner 2FA Approval
            {owner2FAEnabled && (
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                Active
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Require owner/primary admin 2FA approval for sensitive actions like voids, refunds, and role changes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div>
              <Label className="font-medium">Enable Owner 2FA Approval</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When enabled, sensitive actions will require owner verification
              </p>
            </div>
            <div className="flex items-center gap-2">
              {saving === 'owner2fa_enabled' && (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              )}
              <Switch
                checked={owner2FASettings.enabled}
                onCheckedChange={(checked) => {
                  setOwner2FASettings({ ...owner2FASettings, enabled: checked });
                  // In production, save this to the database
                }}
                disabled={!canEdit}
              />
            </div>
          </div>

          {owner2FASettings.enabled && (
            <>
              {/* Protected Actions Configuration */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Protected Actions
                </h4>

                {/* Void Orders */}
                <div className="p-4 rounded-lg border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Label className="font-medium">Void Orders</Label>
                    </div>
                    <Select defaultValue="threshold" disabled={!canEdit}>
                      <SelectTrigger className="w-[180px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="never">Never require</SelectItem>
                        <SelectItem value="always">Always require</SelectItem>
                        <SelectItem value="threshold">Above threshold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Threshold:</Label>
                    <div className="relative w-32">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        type="number"
                        value={owner2FASettings.voidThreshold}
                        onChange={(e) => setOwner2FASettings({
                          ...owner2FASettings,
                          voidThreshold: parseInt(e.target.value) || 0,
                        })}
                        className="h-8 pl-7 text-sm"
                        disabled={!canEdit}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Require approval for voids over {formatCurrency(owner2FASettings.voidThreshold)}
                    </span>
                  </div>
                </div>

                {/* Refunds */}
                <div className="p-4 rounded-lg border space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Label className="font-medium">Refunds</Label>
                    </div>
                    <Select defaultValue="full_only" disabled={!canEdit}>
                      <SelectTrigger className="w-[180px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="never">Never require</SelectItem>
                        <SelectItem value="always">Always require</SelectItem>
                        <SelectItem value="full_only">Full refunds only</SelectItem>
                        <SelectItem value="threshold">Above threshold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Threshold:</Label>
                    <div className="relative w-32">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        type="number"
                        value={owner2FASettings.refundThreshold}
                        onChange={(e) => setOwner2FASettings({
                          ...owner2FASettings,
                          refundThreshold: parseInt(e.target.value) || 0,
                        })}
                        className="h-8 pl-7 text-sm"
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </div>

                {/* Role Changes */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <UserCog className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="font-medium">Role Changes</Label>
                      <p className="text-xs text-muted-foreground">
                        Promotions to Admin/Owner roles
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={owner2FASettings.requireForRoleChanges}
                    onCheckedChange={(checked) => setOwner2FASettings({
                      ...owner2FASettings,
                      requireForRoleChanges: checked,
                    })}
                    disabled={!canEdit}
                  />
                </div>

                {/* 2FA Disable */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="font-medium">Disable 2FA</Label>
                      <p className="text-xs text-muted-foreground">
                        When users disable their own 2FA
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={owner2FASettings.requireFor2FADisable}
                    onCheckedChange={(checked) => setOwner2FASettings({
                      ...owner2FASettings,
                      requireFor2FADisable: checked,
                    })}
                    disabled={!canEdit}
                  />
                </div>
              </div>

              {/* Coming Soon: Push Notifications */}
              <div className="p-4 rounded-lg border border-dashed bg-muted/20 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                  <Label className="font-medium text-muted-foreground">Push Notification Approvals</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Allow owners to approve requests remotely via push notification on their mobile device
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Note */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm text-muted-foreground">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-foreground">About Security Settings</p>
          <p className="mt-1">
            These settings affect how users authenticate and switch between accounts.
            Higher privilege roles (Admin, Owner) always require full login access for security reasons.
            Individual user permissions can still be overridden from the Team page.
          </p>
        </div>
      </div>
    </div>
  );
}

